import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, AlertCircle, CheckCircle, GraduationCap, Users } from 'lucide-react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { createUserProfile, getUserProfile } from '../../firebase/collections';
import { useRealtime } from '../../contexts/RealtimeContext';
import { signInWithGoogle } from '../../utils/googleAuth';
import { createMissingUserProfile } from '../../utils/createMissingUserProfile';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'student' // Default role
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isInitialized } = useRealtime();

  // Redirect if user is already authenticated
  useEffect(() => {
    if (isInitialized && currentUser) {
      // Check if user is admin and redirect accordingly
      const isAdmin = currentUser.email?.includes('admin') || currentUser.email?.includes('mahstry');
      const from = location.state?.from?.pathname || (isAdmin ? '/admin' : '/dashboard');
      navigate(from, { replace: true });
    }
  }, [currentUser, isInitialized, navigate, location.state]);

  // Show loading while checking authentication
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    const { name, email, password, confirmPassword } = formData;

    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const { name, email, password } = formData;

      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update user profile with display name
      await updateProfile(user, {
        displayName: name
      });

      // Create user profile document in Firestore
      const { role } = formData;
      // Determine final role (admin check or selected role)
      const finalRole = email.includes('admin') || email.includes('admin@') ? 'admin' : role;
      
      console.log('🔐 Registration - Creating profile with role:', {
        selectedRole: role,
        finalRole: finalRole,
        email: email
      });
      
      const userProfileData = {
        uid: user.uid,
        email: user.email,
        displayName: name,
        name: name,
        role: finalRole, // Use finalRole for consistency
        userType: finalRole, // IMPORTANT: Set userType same as role for consistency
        isActive: true,
        profileCompleted: false,
        performance: {
          totalTestsTaken: 0,
          averageScore: 0,
          totalScore: 0,
          totalCorrectAnswers: 0,
          totalQuestions: 0,
          lastTestDate: null
        },
        preferences: {
          theme: 'light',
          notifications: true,
          emailNotifications: true
        },
        // Role-specific data
        ...(finalRole === 'teacher' && {
          teacherProfile: {
            subjects: [],
            experience: 0,
            qualifications: [],
            batches: [],
            salary: {
              amount: 0,
              currency: 'INR',
              paymentSchedule: 'monthly'
            },
            attendance: {
              totalDays: 0,
              presentDays: 0,
              leaves: []
            }
          }
        }),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const profileResult = await createUserProfile(userProfileData);
      
      console.log('📝 Profile creation result:', {
        success: profileResult.success,
        error: profileResult.error,
        dataIncluded: {
          role: userProfileData.role,
          userType: userProfileData.userType
        }
      });
      
      if (!profileResult.success) {
        // Try to create a minimal profile to ensure the document exists
        // IMPORTANT: Preserve the selected role even in fallback
        try {
          const minimalProfile = {
            uid: user.uid,
            email: user.email,
            displayName: name,
            name: name,
            role: finalRole, // Preserve the selected role
            userType: finalRole, // Preserve the userType
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          await createUserProfile(minimalProfile);
          console.log('✅ Minimal profile created with role:', finalRole);
        } catch (retryError) {
          // Silent fail - user is already created in auth
          console.error('❌ Failed to create minimal profile:', retryError);
        }
      }

      // Verify the profile was created with correct role
      try {
        const verifyResult = await getUserProfile(user.uid);
        if (verifyResult.success) {
          const savedRole = verifyResult.data.role;
          const savedUserType = verifyResult.data.userType;
          console.log('✅ Profile verification:', { 
            expectedRole: finalRole, 
            savedRole, 
            savedUserType,
            match: savedRole === finalRole && savedUserType === finalRole
          });
          
          // If role doesn't match, try to fix it immediately
          if (savedRole !== finalRole || savedUserType !== finalRole) {
            console.warn('⚠️ Role mismatch detected, fixing...');
            const { updateUserProfile } = await import('../../firebase/collections');
            await updateUserProfile(user.uid, {
              role: finalRole,
              userType: finalRole
            });
            console.log('✅ Role corrected to:', finalRole);
          }
        }
      } catch (verifyError) {
        console.error('❌ Profile verification failed:', verifyError);
      }

      setSuccess('Account created successfully! Redirecting...');

      // Role-based redirect
      if (email.includes('admin') || email.includes('admin@')) {
        // Admin redirect
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1500);
      } else if (formData.role === 'teacher') {
        // Teacher redirect
        setTimeout(() => {
          navigate('/teacher/dashboard');
        }, 1500);
      } else {
        // Student redirect
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      }

    } catch (error) {
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('An account with this email already exists. Please sign in instead, or use Google sign-in if you created your account with Google.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please choose a stronger password');
          break;
        case 'auth/operation-not-allowed':
          setError('Email/password accounts are not enabled. Please contact support');
          break;
        default:
          setError('Registration failed. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      setSuccess('Signing in with Google...');
      const result = await signInWithGoogle();

      if (result.success) {
        const user = result.user;
        let profileResult = result.profile 
          ? { success: true, data: result.profile }
          : await getUserProfile(user.uid);

        // If profile doesn't exist, create it
        if (!profileResult.success) {
          const createResult = await createMissingUserProfile(user);
          if (createResult.success) {
            profileResult = { success: true, data: createResult.data };
          }
        }

        setSuccess('Account created successfully! Redirecting...');

        // Determine redirect path based on role
        let redirectPath = '/dashboard';
        if (profileResult?.success) {
          const userProfile = profileResult.data;
          const isCoachingAdmin = userProfile.role === 'coaching_admin';
          const isAdmin = userProfile.role === 'admin' || 
                         userProfile.role === 'superadmin' || 
                         userProfile.role === 'founder' || 
                         userProfile.isAdmin === true;
          
          if (isCoachingAdmin) {
            redirectPath = '/coaching-admin';
          } else if (isAdmin) {
            redirectPath = '/admin';
          } else if (userProfile.role === 'teacher' || userProfile.userType === 'teacher') {
            redirectPath = '/teacher/dashboard';
          }
        } else if (user.email?.includes('admin') || user.email?.includes('mahstry')) {
          redirectPath = '/admin/dashboard';
        }

        setTimeout(() => {
          navigate(redirectPath);
        }, 1500);
      } else {
        setError(result.error || 'Failed to sign in with Google');
        setLoading(false);
      }
    } catch (error) {
      setError(error.message || 'Failed to sign in with Google');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
          <p className="text-gray-600">Join us and start your learning journey</p>
        </div>

        {/* Register Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                I am a...
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role: 'student' }))}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.role === 'student'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <GraduationCap className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">Student</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role: 'teacher' }))}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    formData.role === 'teacher'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Users className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">Teacher</span>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Institutes have a separate onboarding process. Please contact support for institute registration.
              </p>
            </div>

            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  placeholder="Enter your full name"
                  required
                />
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  placeholder="Create a password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Password must be at least 6 characters long</p>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  placeholder="Confirm your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg"
              >
                <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                <span className="text-red-800 text-sm">{error}</span>
              </motion.div>
            )}

            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                <span className="text-green-800 text-sm">{success}</span>
              </motion.div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-6 mb-6 flex items-center">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-4 text-sm text-gray-500">OR</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          {/* Google Sign In Button */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>Continue with Google</span>
          </button>

          {/* Additional Links */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-green-600 hover:text-green-700 font-medium">
                Sign in
              </Link>
            </p>
          </div>

        </div>
      </motion.div>
    </div>
  );
};

export default Register;
